import { forwardRef } from 'react'

import { cn } from '@/lib/utils'

import { FxIcon } from './FxIcon'

/**
 * The big "Neues Musterformular" tile that opens the overview: `primary`,
 * `radius-sm`, a white `assignment_add` illustration and the label in
 * `tileTitle`. 482x200 on desktop; `compact` drops the label for phones,
 * where the icon alone fills a full-width tile.
 *
 * It is full width below the `xs` frame because the phone artboards draw it
 * that way, and the breakpoints are min-width: the phone layout is the base.
 *
 * `href` is an addition to the design system's contract — most artboards make
 * the tile a link to the upload dialog's route, and a link that navigates
 * must be an anchor so it opens in a new tab and shows its target.
 */
export interface FxCreateTileProps extends React.HTMLAttributes<HTMLElement> {
  label?: string
  compact?: boolean
  /** Renders the tile as a link instead of a button. */
  href?: string
  type?: 'button' | 'submit'
}

export const FxCreateTile = forwardRef<HTMLElement, FxCreateTileProps>(
  ({ label = 'Neues Musterformular', compact, href, type = 'button', className, ...props }, ref) => {
    const content = (
      <>
        <FxIcon name="assignment_add" size={compact ? 72 : 80} />
        {!compact && <span className="max-w-[200px] text-left text-tile-title">{label}</span>}
      </>
    )
    const styles = cn(
      'flex w-full items-center justify-center gap-8 rounded-sm bg-primary text-on-primary no-underline shadow-raised hover:bg-primary-pressed',
      compact ? 'h-[132px] xs:w-[280px]' : 'h-[200px] xs:w-[482px]',
      className,
    )
    // The compact tile shows no label, so the accessible name has to come
    // from the attribute; the full one already has its label as text.
    const srLabel = compact ? label : undefined

    if (href) {
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          className={styles}
          aria-label={srLabel}
          {...props}
        >
          {content}
        </a>
      )
    }
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type={type}
        className={styles}
        aria-label={srLabel}
        {...props}
      >
        {content}
      </button>
    )
  },
)
FxCreateTile.displayName = 'FxCreateTile'
